import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  ChangeDetectorRef,
  signal,
} from "@angular/core";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import {
  FREE,
  LIFETIME,
  PlanOptions,
  YEAR,
} from "@bitwarden/web-vault/app/models/types/subscription.type";
import { StripeConfigService } from "@bitwarden/web-vault/app/services/api/stripe-config.service";
import Stripe from "stripe";
import { GlossRoutes } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import {
  PaymentMethod,
  PaymentHistoryCharge,
  PaymentHistoryInvoice,
} from "@bitwarden/web-vault/app/models/types/payment.types";
import { AppleConfigService } from "@bitwarden/web-vault/app/services/api/apple-config.service";

@Component({
  selector: "app-billing",
  templateUrl: "./subscription.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionPageComponent implements OnInit {
  protected readonly FREE = FREE;
  protected userStoreService = inject(UserStoreService);
  protected stripeConfigService = inject(StripeConfigService);
  protected confirmationDialogService = inject(ConfirmationDialogService);
  protected readonly PlanOptions = PlanOptions;
  protected cdr = inject(ChangeDetectorRef);
  protected appleConfigService: AppleConfigService = inject(AppleConfigService);

  protected paymentMethodsLoad = signal<PaymentMethod[]>([]);
  protected paymentHistoryCharges = signal<PaymentHistoryCharge[]>([]);
  protected paymentHistoryInvoices = signal<PaymentHistoryInvoice[]>([]);
  protected fetchingPaymentMethod = signal<boolean>(false);
  protected fetchingPaymentHistory = signal<boolean>(false);

  protected hasValidPaymentMethod = computed(() => {
    const methods = this.paymentMethodsLoad();
    return methods.length > 0 && methods[0]?.status === "valid";
  });

  protected isFetchingAny = computed(() => {
    return this.fetchingPaymentMethod() || this.fetchingPaymentHistory();
  });

  get activeSubscription() {
    return this.userStoreService.subscription.activeSubscription;
  }

  protected activeSubscriptionLabel = computed(() => {
    // null before the subscription loads and after logout (clear()); that's "no paid plan",
    // so Free, not the Premium Monthly fall-through below.
    const paymentCycle = this.activeSubscription()?.paymentCycle;
    if (!paymentCycle || paymentCycle === LIFETIME) {
      return PlanOptions.free.label;
    }
    return paymentCycle === YEAR
      ? PlanOptions.premiumAnnual.label
      : PlanOptions.premiumMonthly.label;
  });

  async ngOnInit() {
    await this.fetchPaymentMethods();
    await this.onFetchPaymentHistory();
  }

  async fetchPaymentMethods() {
    this.fetchingPaymentMethod.set(true);
    if (!this.appleConfigService.iOSIAPCapability) {
      const raw = await this.stripeConfigService.getPaymentMethods();
      if (raw) {
        const methods = Array.isArray(raw) ? raw : [raw];
        this.paymentMethodsLoad.set(
          methods
            .filter((m) => m && m.type === "card")
            .map((m) => ({
              type: m.type,
              card: {
                brand: m.card.brand,
                last4: m.card.last4,
                exp_month: m.card.exp_month,
                exp_year: m.card.exp_year,
                checks: m.card.checks,
              },
              /** todo: need to check if stripe provides the valid status, or do we need to manage it in the backend, at the moment of writing this, the return object doesnt provide a status field
               so we assume when we can fetch a payment method, it is valid (if no payment method found or the payment method is not default, the backend returns null atm) **/
              status: "valid",
            })),
        );
      } else {
        this.paymentMethodsLoad.set([]);
      }
    }
    this.fetchingPaymentMethod.set(false);
    this.cdr.markForCheck();
  }

  /** "08 / 2028", as shown under the saved card. */
  protected formatExpiry(card: PaymentMethod["card"]): string {
    return `${String(card.exp_month).padStart(2, "0")} / ${card.exp_year}`;
  }

  async onAddPaymentDetails() {
    const payload: Stripe.Checkout.SessionCreateParams = {
      mode: "setup",
      payment_method_types: ["card"],
      success_url: this.stripeConfigService.redirectUrl + "/#/" + `${GlossRoutes.Views}`,
      cancel_url: this.stripeConfigService.redirectUrl + "/#/" + `${GlossRoutes.Views}`,
    };
    try {
      const result = await this.stripeConfigService.createCheckoutSessions(payload);
      if (result && result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      this.confirmationDialogService.notify("error", "errorOccurred", "failedToAddPaymentMethod");
    }
  }

  async onDeletePaymentMethod() {
    const confirmed = await this.confirmationDialogService.confirmFor(
      ConfirmationEnum.deletingPaymentMethod,
    );
    if (!confirmed) {
      return;
    }
    try {
      await this.stripeConfigService.deletePaymentMethod();
      // Refresh payment methods after deletion
      await this.fetchPaymentMethods();
    } catch (error) {
      this.confirmationDialogService.notify(
        "error",
        "errorOccurred",
        "failedToDeletePaymentMethod",
      );
    }
  }

  async onFetchPaymentHistory() {
    this.fetchingPaymentHistory.set(true);
    let response: { charges?: any[]; invoices?: any[] } | null = null;
    try {
      if (this.appleConfigService.iOSIAPCapability) {
        response = await this.appleConfigService.getPaymentHistory();
      } else {
        response = await this.stripeConfigService.getPaymentHistory();
      }
      // Store the charges and invoices separately
      this.paymentHistoryCharges.set(response?.charges || []);
      this.paymentHistoryInvoices.set(response?.invoices || []);
    } catch (error) {
      this.confirmationDialogService.notify(
        "error",
        "errorOccurred",
        "Failed to fetch payment history. The endpoint may not be deployed yet.",
        false,
      );
    } finally {
      this.fetchingPaymentHistory.set(false);
    }
  }
}
